import { Table, Field, Schema } from 'apache-arrow/Arrow';
import { TableSchema } from './types';
import { fieldToColumnSchema } from './column-utils';

/**
 * Wraps an Apache Arrow Table to provide a consistent interface for table operations
 * @memoryUsage This class is memory intensive:
 * - Holds all data rows in memory
 * - Memory usage scales with row count × column count
 * - Avoid unnecessary copies of instances
 * - Consider using views or streaming for large datasets
 * - Only materialize when needed for visualization or export
 */
export class MaterializedTableData {
  private arrowTable: Table;

  constructor(data: Table) {
    this.arrowTable = data;
  }

  /**
   * Get the underlying Arrow Table
   * @performance Note: Accessing the full table can be memory intensive
   * for large datasets as it may require loading all data into memory
   */
  get table(): Table {
    return this.arrowTable;
  }

  /**
   * Get the schema of the table
   * @performance Moderate cost for tables with many columns:
   * - Processes every column in the schema
   * - Creates new schema objects
   * - Cost scales linearly with number of columns
   */
  get schema(): TableSchema {
    return this.extractSchema(this.arrowTable);
  }

  /**
   * Get the number of rows in the table
   */
  get rowCount(): number {
    return this.arrowTable.numRows;
  }

  /**
   * Extract TableSchema from Arrow Table
   * @performance Cost scales linearly with number of columns
   * Consider caching the result if called frequently
   */
  private extractSchema(table: Table): TableSchema {
    const fields = table.schema.fields;
    return {
      columns: fields.map((field: Field) => fieldToColumnSchema(field))
    };
  }

  /**
   * Create an empty table with the given schema
   */
  static empty(schema: Schema): MaterializedTableData {
    // Create an empty table directly with the schema
    return new MaterializedTableData(new Table(schema));
  }
} 